---
title: "QR-код для Wi-Fi: делайте для гостевой сети, не для основного пароля"
description: "Как создать QR-код для Wi-Fi, почему лучше использовать гостевую сеть и что проверить перед печатью кода в офисе, кафе или квартире."
locale: "ru"
date: "2026-07-01"
tags: ["qr", "wifi", "guest-network"]
relatedTools: ["qr-generator"]
translations:
  en: "wifi-qr-code-for-guest-network"
faq:
  - q: "Можно ли изменить пароль Wi-Fi в уже напечатанном QR?"
    a: "Нет. Wi-Fi данные записаны в статический QR. Если пароль изменился, нужно сгенерировать и распечатать новый код."
  - q: "Безопасно ли печатать QR-код с паролем Wi-Fi?"
    a: "Безопаснее делать это для гостевой сети. Любой человек, который видит QR-код или фото кода, получает данные подключения; фактическое подключение ещё зависит от зоны действия сети."
  - q: "QR-код Wi-Fi отправляется на сервер?"
    a: "В qrka генерация QR идёт локально в браузере через qrcode. Содержимое Wi-Fi QR не нужно отправлять на backend."
---

На стойке кафе лежит табличка: `Wi-Fi: CafeGuest`, пароль мелким шрифтом
на 14 символов. Каждый второй гость спрашивает пароль ещё раз, кто-то
путает `O` и `0`, администратор диктует вслух. QR-код решает это за
минуту, но только если не зашить туда основной пароль от рабочей сети.

[Генератор QR-кодов](/qr-kod) умеет Wi-Fi QR: выбираете тип Wi-Fi,
вводите SSID, тип защиты и пароль, скачиваете PNG или SVG. Код
получается статическим. Телефон считывает его камерой и предлагает
подключиться к сети.

## Что попадёт в QR

Wi-Fi QR хранит данные подключения прямо в тексте кода: имя сети,
тип защиты и пароль. Это удобно, но не секретно. QR на стене, фото в
чате или распечатка на столе раскрывают пароль всем, кто видит код.

Поэтому нормальная схема для офиса, кафе, студии или квартиры с гостями:
создать отдельную guest network, ограничить доступ к локальным устройствам
и печатать QR именно для неё. Основные Wi-Fi credentials, через которые
открывается путь к роутеру, NAS, кассе, принтеру или рабочей
инфраструктуре, в QR лучше не класть.

## Перед печатью

| Проверка | Что сделать |
| --- | --- |
| Сеть отдельная | Используйте guest network, не основной SSID |
| Пароль свежий | Не печатайте пароль, который уже знают подрядчики и бывшие сотрудники |
| Контраст нормальный | Чёрный код на белом фоне сканируется надёжнее цветных экспериментов |
| Размер подходит | Для стола и стены нужны разные размеры |
| Код проверен | Отсканируйте телефоном до печати тиража |

Если меняете пароль раз в месяц, QR тоже придётся менять раз в месяц.
Статический код нельзя отредактировать после печати. В этом он честнее
динамических сервисов: нет redirect-сервера и аккаунта, но нет и магии
обновления.

## PNG или SVG

Для маленькой таблички на ресепшене достаточно PNG с высоким размером.
Для макета в типографии удобнее SVG: он масштабируется без размытия и
лучше переживает верстку. В обоих случаях оставьте белое поле вокруг
матрицы. Если дизайнер обрезал quiet zone "чтобы было ровнее", камера
может начать ошибаться.

Цветной QR тоже возможен, но с Wi-Fi лучше не геройствовать. Человек
сканирует код один раз, обычно на ходу, под бликом лампы или с экрана
ноутбука. Контраст важнее фирменного оттенка.

## Когда QR лучше не печатать

Если сеть даёт доступ к внутренним панелям, файловым шарам, кассе,
камерам или админке роутера, QR на стене превращает пароль в публичную
информацию. В таком сценарии сначала настраивают гостевую сеть и
изоляцию клиентов, потом делают QR.

Сам код можно создать локально в браузере. Но безопасность Wi-Fi решает
не генератор, а то, какую сеть вы решили показать всем.
